import type { Router } from 'vue-router'
import { isBotUserAgent, isDevMode } from '@/utils/utils'

const ENDPOINT = '/api/events'
const SCROLL_BUCKETS = [100, 75, 50, 25]

type EventId = 'page_view' | 'scroll_depth' | 'time_on_page' | 'button_click'

type ScrollTarget = HTMLElement | Window

interface View {
    pageId: string
    scrollTarget: ScrollTarget
    visibleSince: number | null
    visibleMs: number
    maxScrollPercent: number
}

interface I18nLike {
    global: { locale: { value: string } }
}

let enabled = false
let i18n: I18nLike | null = null
let view: View | null = null
let scrollFramePending = false

function lang(): string {
    return i18n?.global.locale.value === 'en' ? 'en' : 'sl'
}

/**
 * Beacons are fire-and-forget. sendBeacon cannot set headers, so the body goes
 * as text/plain, which is CORS-safelisted and needs no preflight.
 */
function send(events: { pageId: string; eventId: EventId; value: string }[]): void {
    if (!enabled || !events.length) return

    try {
        const body = JSON.stringify(events.map(event => ({ ...event, lang: lang() })))

        if (navigator.sendBeacon?.(ENDPOINT, new Blob([body], { type: 'text/plain' }))) return

        fetch(ENDPOINT, {
            method: 'POST',
            body,
            keepalive: true,
            headers: { 'Content-Type': 'text/plain' },
        }).catch(() => undefined)
    } catch {
        // Tracking must never break the page.
    }
}

function scrollPercent(target: ScrollTarget): number {
    const [scrollTop, clientHeight, scrollHeight] =
        target === window
            ? [window.scrollY, window.innerHeight, document.documentElement.scrollHeight]
            : [
                  (target as HTMLElement).scrollTop,
                  (target as HTMLElement).clientHeight,
                  (target as HTMLElement).scrollHeight,
              ]

    // Content that fits without scrolling was seen in full.
    if (scrollHeight <= clientHeight) return 100

    const percent = ((scrollTop + clientHeight) / scrollHeight) * 100
    return Math.min(100, Math.max(0, Math.round(percent)))
}

/** Highest threshold reached, or 0 for a visitor who never scrolled. */
function scrollBucket(percent: number): number {
    return SCROLL_BUCKETS.find(bucket => percent >= bucket) ?? 0
}

function onScroll(): void {
    if (scrollFramePending) return

    scrollFramePending = true
    requestAnimationFrame(() => {
        scrollFramePending = false
        if (!view) return

        view.maxScrollPercent = Math.max(view.maxScrollPercent, scrollPercent(view.scrollTarget))
    })
}

function accumulateVisibleTime(): void {
    if (!view || view.visibleSince === null) return

    view.visibleMs += Date.now() - view.visibleSince
    view.visibleSince = null
}

/**
 * Flushes engagement metrics for the current view. A view is flushed exactly
 * once, on whichever comes first: the tab being hidden, a route change, a
 * modal closing, or the page going away.
 */
export function endView(): void {
    if (!view) return

    accumulateVisibleTime()

    const { pageId, visibleMs, maxScrollPercent, scrollTarget } = view

    scrollTarget.removeEventListener('scroll', onScroll)
    view = null

    send([
        { pageId, eventId: 'time_on_page', value: String(Math.round(visibleMs / 1000)) },
        { pageId, eventId: 'scroll_depth', value: String(scrollBucket(maxScrollPercent)) },
    ])
}

function beginView(pageId: string, scrollTarget: ScrollTarget): void {
    endView()

    // Own visits to the admin page would otherwise show up as visitor traffic.
    if (!enabled || pageId === 'admin') return

    view = {
        pageId,
        scrollTarget,
        visibleSince: document.visibilityState === 'visible' ? Date.now() : null,
        visibleMs: 0,
        maxScrollPercent: scrollPercent(scrollTarget),
    }

    scrollTarget.addEventListener('scroll', onScroll, { passive: true })
}

/** A new page view: emits `page_view` and starts measuring engagement. */
export function startView(pageId: string, scrollTarget: ScrollTarget = window): void {
    beginView(pageId, scrollTarget)

    if (view) send([{ pageId, eventId: 'page_view', value: '' }])
}

/**
 * Picks measurement back up on a page the visitor has already been counted on,
 * such as the homepage after a modal closes, without counting it twice.
 */
export function resumeView(pageId: string, scrollTarget: ScrollTarget = window): void {
    beginView(pageId, scrollTarget)
}

/** Page the visitor is currently on, so click handlers need not know it. */
export function currentPageId(): string | null {
    return view?.pageId ?? null
}

export function trackClick(target: string, pageId = currentPageId()): void {
    if (!pageId) return

    send([{ pageId, eventId: 'button_click', value: target }])
}

/** Route name to page id: the English routes are the Slovenian ones plus '-en'. */
export function pageIdFromRoute(name: unknown): string | null {
    if (typeof name !== 'string') return null

    return name.endsWith('-en') ? name.slice(0, -3) : name
}

export function initAnalytics(router: Router, i18nInstance: I18nLike): void {
    i18n = i18nInstance
    // navigator.webdriver covers the Playwright suite; dev mode matters because
    // several components post to the production API even from localhost.
    enabled = !isDevMode() && !navigator.webdriver && !isBotUserAgent()

    if (!enabled) return

    router.afterEach(to => {
        const pageId = pageIdFromRoute(to.name)
        if (pageId) startView(pageId)
    })

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            endView()
        } else if (view && view.visibleSince === null) {
            view.visibleSince = Date.now()
        }
    })

    // Safari and Chrome on mobile often skip beforeunload entirely.
    window.addEventListener('pagehide', endView)
}
